import { ReactElement, ReactNode } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Alert, Button, Stack } from '@mui/material'
import { CheckSettingsFormValues } from '../types'

interface Props {
  /** Ensures the staged settings carry the section's object so its fields never read null. */
  normalizeSettings: (settings: SettingsOnMember) => SettingsOnMember
  /** German warning shown while the selected members differ, e.g. 'Elemente haben unterschiedliche …'. */
  differingSettingsMessage: string
  changeButtonDataCy?: string
  /** The section's form fields, registered under the `sharedSettings` path. */
  children: ReactNode
}

/**
 * Renders a section's fields once the selected members share a value for it.
 * While they differ there is nothing sensible to prefill, so the form offers to
 * overwrite them with the first member's settings instead.
 */
const SharedSettingsFields = ({
  normalizeSettings,
  differingSettingsMessage,
  changeButtonDataCy,
  children,
}: Props): ReactElement => {
  const { getValues, setValue } = useFormContext<CheckSettingsFormValues>()
  const sharedSettings = useWatch<CheckSettingsFormValues>({ name: 'sharedSettings' })

  if (!sharedSettings) {
    const firstElement = getValues('elements')[0]

    return (
      <Stack direction="column" spacing={1}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={!firstElement}
          onClick={() =>
            setValue('sharedSettings', normalizeSettings(firstElement.checkSettings), {
              shouldDirty: true,
              shouldTouch: true,
            })
          }
          data-cy={changeButtonDataCy}
        >
          Ändern
        </Button>
        <Alert severity="warning">{differingSettingsMessage}</Alert>
      </Stack>
    )
  }

  return <>{children}</>
}

export default SharedSettingsFields
